import { useRef, useState } from 'react'
import { useModalShell } from './useModalShell'
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { fetchContent, text } from '../api/content'
import {
  declareDayOut,
  coverSummary,
  fetchColleagues,
  fetchTeacherDay,
  type Colleague,
  type CoverRow,
  type DaySession,
  type DeclaredDayResult,
  type DeclaredDaySession,
} from '../api/cover'
import { clockTime, meridiem } from '../api/attendance'
import { formatFullDate, today } from '../api/calendar'
import { AskSomebody, CallItOff, CalledOff, ComingAfterAll } from './CoverActions'

/**
 * Everything a declared day makes stale (D-683).
 *
 * **One list, because there are three doors into this dialog** — her portal,
 * a conversation, and her own account page — and they were three hand-kept
 * copies. The copy on `/account` grew `my-time-off`, because the time-off
 * panel's count of what is left to arrange is exactly what declaring a day
 * changes; the other two did not, so sorting a class out from `/teacher` and
 * walking back to `/account` inside the 30-second `staleTime` re-read the old
 * number and offered a dialog with every row already struck through. Found by
 * an audit, one round after the same audit called the duplication cosmetic.
 *
 * **It exists at all only because of `onSorted`.** The bulk submit below
 * invalidates *everything* and needs none of this; the per-row controls — ask
 * somebody, call it off, coming after all — call `onDone` on their own, and
 * that is the path these keys carry. A caller that forgets one gets a screen
 * that is quietly a version behind, which is the hardest kind of wrong to
 * notice.
 */
export function refreshAfterDeclaring(queryClient: QueryClient) {
  for (const key of [
    ['my-time-off'],
    ['cover'],
    ['teacher-home'],
    ['teacher-day'],
    ['sheet'],
    ['registers'],
    ['notifications'],
  ]) {
    queryClient.invalidateQueries({ queryKey: key })
  }
}

/**
 * What she wants done with one class she will miss (D-361).
 *
 * ⚠ **This dialog is no longer the only way to call a night off, and the two
 * differ on purpose** (D-890, D-891). Since D-890 a teacher calls one night off
 * from the **register**, naming a reason, **without declaring herself absent** —
 * because the driving case is the *dancer* being unavailable, not her. The
 * endpoint never required a declaration; only this screen did.
 *
 * *Cancel it* here still writes one, and that is right: **this dialog's premise
 * is "I am out on Tuesday."** Do not make either match the other. The reasoning
 * is D-891; the feature is
 * `docs/specs/2026-10-12-call-off-one-session.md`. *(That spec also owes a
 * correction to `docs/specs/2026-08-26-cover-in-one-pass.md`, which still
 * describes this as the only order — it is legacy-shaped, so `H42` refuses an
 * edit to it until it carries the four sections, and `docs/GAPS.md` holds that
 * row.)*
 *
 * **Three, not two flags.** `ask` without a name is still a declaration in
 * Tonya's queue, which is why it is a separate kind rather than "cover with a
 * name attached" — she can mean *I have somebody in mind* and not have picked
 * yet, and the row should not silently become something else while she decides.
 */
type Choice = 'cover' | 'ask' | 'cancel'

const CHOICES: { value: Choice; label: string }[] = [
  { value: 'cover', label: 'Needs cover' },
  { value: 'ask', label: 'Ask someone' },
  { value: 'cancel', label: 'Cancel it' },
]

/**
 * A whole day she will be out for, in one pass (D-354).
 *
 * **Owner:** *"do multiple classes at a time since a teacher can have 8 or more
 * classes they need to schedule stuff for."* The control this replaces declared
 * one occurrence at a time from a picker capped at a fortnight, so a Monday in
 * October could not be reached at all and a twelve-class day meant opening the
 * dialog twelve times.
 *
 * **The grammar is the parent's bulk absence, deliberately** (`ReportAbsence`):
 * pick a date, the server works out which classes, everything is ticked to
 * begin with, and a row already handled is shown struck through rather than
 * dropped. Two personas asking the same shape of question should not have to
 * learn two shapes of answer. **The composition is shared, not the component** —
 * different subjects against different tables, so what is reused is the
 * grammar (D-199 answered by sharing the shape rather than forcing one screen
 * to serve both).
 *
 * **A tick says *I am out*. It never cancels a class.** A ticked row with
 * nobody named is declared and *uncovered*, which is what puts it in Tonya's
 * queue — D-328's commonest sentence is *"I am out on Thursday and I do not
 * know who can take it."* Naming a coverer stays on the per-session control in
 * `CoverPanel`, because a ballet sub cannot take tap and one name for twelve
 * rows would be a lie that is quicker to type.
 *
 * **Sized for two rows and able to hold twelve.** `observed`: only three
 * teachers ever reach eight sessions in a day, across 125 of 1,759 teacher-days
 * — so the list scrolls inside the dialog rather than the dialog growing to fit
 * the rare case.
 */
export default function DeclareOutDay({
  onClose,
  onDone,
  initialDate,
  initialNote,
  onRaised,
}: {
  onClose: () => void
  onDone: () => void
  /**
   * Her words, carried in from a conversation (D-630): the note every
   * offerable row opens with, still hers to change per row. A row she edits
   * keeps her edit; only an untouched row reads the draft.
   */
  initialNote?: string
  /**
   * What the day wrote, row by row, once it has (D-630) - the absence and the
   * ask - so a conversation can pin the absence. After `onDone`, never
   * instead of it.
   */
  onRaised?: (sessions: DeclaredDaySession[]) => void
  /**
   * The day to open on, when something already knows which (D-365).
   *
   * **The quiet line on home names a date**, and a link that lands somewhere
   * unable to answer the question it was about is D-360's fault shape — she
   * would arrive on today and have to re-find the Thursday she was just told
   * about. Absent, it opens on today, which is right for the button on her
   * week.
   */
  initialDate?: string
}) {
  const [date, setDate] = useState(initialDate ?? today())

  /*
   * Which rows she has *un*ticked, rather than which she has ticked.
   *
   * Everything tickable starts ticked — "I am out on Thursday" is the whole
   * sentence, and making her tick twelve boxes to say it is the complaint this
   * control exists to answer. Holding the exceptions means a day that loads
   * after she has already changed her mind does not silently re-tick.
   */
  const [dropped, setDropped] = useState<string[]>([])

  /*
   * **One choice per row, defaulted, and held as one value rather than two
   * flags** (D-361).
   *
   * Owner: *"one that says like 'cancel class' ... and another column that is
   * like find a sub"*. Two columns is how he described it and one choice is
   * what it has to be: cancelling withdraws every live cover request for the
   * session (D-355) and `/accept` refuses a cancelled one, so two independent
   * tick-boxes would let her express a state the server cannot hold — and the
   * server would discard half of it without saying so.
   *
   * Absent means *needs cover*, which is the same default a tick has always
   * carried: **"I am out on Thursday and I do not know who can take it"** is
   * D-328's commonest sentence, and it must never drift into a cancellation by
   * omission.
   */
  const [choices, setChoices] = useState<
    Record<string, { kind: Choice; staffId?: string; note?: string }>
  >({})

  const kindOf = (id: string) => choices[id]?.kind ?? 'cover'

  /*
   * **Every setter carries the other two forward** (D-375). The note joined a
   * shape whose setters each rebuilt the whole entry, so changing the choice
   * after typing would have silently dropped what she wrote — the same reason
   * `setKind` already carried `staffId`.
   */
  const setKind = (id: string, kind: Choice) =>
    setChoices((prev) => ({ ...prev, [id]: { ...prev[id], kind } }))

  const setWho = (id: string, staffId: string) =>
    setChoices((prev) => ({
      ...prev,
      [id]: { ...prev[id], kind: 'ask', staffId: staffId || undefined },
    }))

  const setNoteFor = (id: string, note: string) =>
    setChoices((prev) => ({
      // Spread first: written the other way round the spread overwrites the
      // default and an untouched row loses its 'cover'.
      ...prev,
      [id]: { ...prev[id], kind: prev[id]?.kind ?? 'cover', note },
    }))

  /*
   * Why she is calling classes off. **Tonya reads it and nobody else** (D-355)
   * — which is exactly why it is not the note beside it (D-364).
   */
  const [reason, setReason] = useState('')
  const [familyNote, setFamilyNote] = useState('')
  const [nudges, setNudges] = useState(true)
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  /*
   * The count the server worked out, held between the two taps (D-362).
   *
   * Keyed on having asked for a preview rather than on the response's own
   * `applied` flag: what the second tap needs to know is *what she has already
   * been shown*, and a response cannot answer that.
   *
   * **It carries the submission it describes, and is discarded the moment that
   * submission changes.** Held as a bare result it went stale silently: preview
   * one cancellation, be told *"14 people will be told"*, then set a second row
   * to *Cancel it* and tap again — the second tap saw a non-null confirmation
   * and applied **two** cancellations immediately, against a figure she was
   * shown for one. On the only action in the app that reaches sixty households
   * and cannot be taken back (D-356), consenting to a different submission from
   * the one that runs is the whole failure.
   */
  const [confirming, setConfirming] = useState<{ of: string; result: DeclaredDayResult } | null>(
    null,
  )

  const queryClient = useQueryClient()

  const day = useQuery({
    queryKey: ['teacher-day', date],
    queryFn: () => fetchTeacherDay(date),
    enabled: Boolean(date),
  })

  const sessions = day.data ?? []
  const offerable = sessions.filter((s) => !s.alreadyHandled)
  const chosen = offerable.filter((s) => !dropped.includes(s.occurrenceId))

  /*
   * What the server actually did, when it is not what she asked for.
   *
   * **The endpoint reports a reason per row and the first cut threw them
   * away**, closing on any 200. The day she is looking at can be half an hour
   * stale — react-query serves it for thirty seconds — so *"I am out for 12
   * classes"* could post twelve rows the server had already declared, come back
   * `declared: 0`, and close with nothing said either way. That is the same
   * failure the empty-day guard below refuses on the read path, left open on
   * the write.
   */
  const [outcome, setOutcome] = useState<DeclaredDayResult | null>(null)

  const cancelling = chosen.filter((s) => kindOf(s.occurrenceId) === 'cancel')

  /*
   * **Fetched once the day has somebody to ask, and shared with every row.**
   *
   * One query key for the whole dialog, so twelve rows set to *Ask someone* are
   * still one request — the same key `CoverPanel` uses, and the same lazy
   * `enabled` reasoning: most teachers on most days name nobody, and a list
   * nobody opens is a request nobody needed.
   *
   * **The list is the same one the server will accept.** `/cover/colleagues`
   * already removes the caller, which is what stops the commonest self-ask
   * before the guard has to catch it — a dialog offering an option the API
   * refuses is the fault D-334 named.
   */
  const asking = chosen.some((s) => kindOf(s.occurrenceId) === 'ask')

  const colleagues = useQuery({
    // Per day (D-597): who is free depends on the night.
    queryKey: ['cover', 'colleagues', date],
    queryFn: () => fetchColleagues(date),
    enabled: asking,
  })

  const rows = chosen.map((s) => {
    const choice = choices[s.occurrenceId]
    return {
      occurrenceId: s.occurrenceId,
      // Explicit nulls rather than omitted keys: the endpoint reads a row, and
      // "not asked" is a value it should not have to infer from absence.
      askStaffId: choice?.kind === 'ask' ? (choice.staffId ?? null) : null,
      cancel: choice?.kind === 'cancel',
      /*
       * Per class, because a substitute takes one (D-375). Null on a
       * cancellation — nobody is picking that class up, and its reason travels
       * separately to Tonya (D-364).
       */
      // An untouched row carries the words she arrived with (D-630); a row
      // she has edited, even to nothing, carries her edit.
      note: choice?.kind === 'cancel' ? null : (choice?.note ?? initialNote)?.trim() || null,
    }
  })

  /*
   * **What a held preview is a preview *of*.** Everything the server would act
   * on: the day, the rows and their choices, and the reason that travels with a
   * cancellation. Anything she can change that changes the outcome has to be in
   * here, or the confirmation outlives the thing it described.
   */
  const signature = JSON.stringify({ date, rows, reason: reason.trim(), familyNote: familyNote.trim() })

  const shown = confirming?.of === signature ? confirming.result : null

  const run = useMutation({
    /*
     * **Rows, and one request** (D-361, gate G7). The cancel executes on this
     * endpoint rather than in a client loop over `cancelSession`, so what she
     * confirms and what happens are the same operation — and a dropped signal
     * halfway cannot leave fourteen families told and thirty-three not, with
     * nothing on screen able to say which.
     */
    mutationFn: (apply: boolean) =>
      declareDayOut(date, rows, null, reason.trim() || null, apply, nudges, familyNote.trim() || null),

    onSuccess: (res) => {
      /*
       * **The preview stops here and shows the number** (D-356, D-362).
       *
       * This is the only action in the app that reaches sixty households and
       * cannot be taken back, so the count comes back before anything is sent.
       * A day with nothing to call off never sees this — the ordinary case must
       * not pay for the dangerous one.
       */
      if (!res.applied) {
        setConfirming({ of: signature, result: res })
        return
      }

      /*
       * Her own week and home were still showing the class as hers after
       * she called it off - "it shows cancelled on Tonya and dancer side,
       * just not on the teacher's" - because nothing here told them to look
       * again; they caught up on their own timer. Everything is asked for
       * afresh rather than a list of keys: a declared day touches the
       * calendar, her home, her classes, conflicts, requests and the
       * unfinished-attendance count, and a list is one key short the day a
       * screen is added.
       */
      queryClient.invalidateQueries()

      onDone()
      onRaised?.(res.sessions)
      setConfirming(null)

      /*
       * **Asked of the rows, never of the count** (D-361).
       *
       * This compared `res.declared` with how many she ticked, which stopped
       * being the same question the moment a row could do something other than
       * declare — and it diverged in **both** directions at once. A row refused
       * as already started is still counted (she is out; only the cancel was
       * refused), so twelve of twelve came back and the refusal never reached
       * the screen. A row already declared that cancelled successfully is *not*
       * counted, so a submission that did exactly what she asked reported
       * *"1 of 2 recorded"* over an empty list of reasons.
       *
       * The response already carries the truth per row. Ask it.
       */
      if (!res.sessions.some((s) => s.alreadyHandled)) {
        onClose()
        return
      }

      setOutcome(res)
      void day.refetch()
    },
  })

  // One tap when nothing is being called off, two when something is.
  const submitting = () => run.mutate(shown === null && cancelling.length > 0 ? false : true)

  /*
   * **Anything that changes a row she has already declared refetches the day
   * and tells the rest of the app** (D-365).
   *
   * `onDone` is the same invalidation set the other cover surfaces use, and for
   * the same reason (D-330): asking, withdrawing and calling off all run
   * `ResolveNightAsync`, so whatever names who is teaching a night is stale
   * afterwards — a register naming somebody who has just withdrawn is the
   * defect that set exists to prevent. The local refetch is on top of it,
   * because the dialog is looking at the row that changed.
   */
  const sorted = () => {
    void day.refetch()
    onDone()
  }

  const field =
    'rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13.5px] text-ink outline-none focus:border-accent'

  const panel = useRef<HTMLDivElement>(null)
  const pressedBackdrop = useRef(false)

  useModalShell({ panel, onDismiss: onClose })

  return (
    /*
      <b>The shared contract, in place</b> (D-515). Nothing closed this on
      Escape or on a backdrop tap, and Tab walked straight out of it into the
      page behind — while the dialog beside it on the teacher's own screen
      closed on both. Its markup is left exactly where it is: the panel scrolls
      and holds a twelve-row grid sized for a phone (D-354), and moving it
      inside a shell to gain a keyboard contract would risk the layout that
      whole decision is about.
    */
    <div
      onMouseDown={(e) => {
        pressedBackdrop.current = e.target === e.currentTarget
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && pressedBackdrop.current) onClose()
      }}
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 sm:items-center"
    >
      <div
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Say you are out"
        className="max-h-[90vh] w-full max-w-[32rem] overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 sm:rounded-2xl"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg">Which day are you out?</h2>
            {/*
              **What this covers, and what it does not** (D-354, scenario 32).

              Her schedule carries private lessons and room bookings as well as
              classes, and neither has a `ClassOccurrence` behind it — a booking
              is a reservation rather than a lesson (D-80), and a private is one
              family expecting her through a different flow (D-286). So they are
              not on this grid and are not touched by it.

              **Here rather than under the button, because that is where it was
              and it did not survive the case it exists for.** Rendered on
              Hannah's twelve-row Monday at 390px the caveat sat below the fold:
              present on a quiet day, invisible on the busy one where somebody
              might actually believe the day was handled. Found by looking at
              it, which is the only way this kind of thing is ever found.
            */}
            <p className="mt-0.5 text-[12.5px] text-ink-3">
              Pick the day and we will list your classes. Untick anything you will still make.
              Private lessons and room bookings are not on this list.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid size-7 shrink-0 place-items-center rounded-lg text-ink-3 hover:bg-surface-2 hover:text-ink"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-semibold text-ink-3">The day</span>
            <input
              type="date"
              value={date}
              onChange={(e) => {
                setDate(e.target.value)
                setOutcome(null)
                // A different day is a different set of rows; carrying the
                // exceptions over would untick a class she never saw.
                setDropped([])
              }}
              className={field}
            />
          </label>

          {/*
            **The note moved onto the rows** (D-375, amending D-364).

            D-364 named this field for who actually reads it — the colleague who
            picks the class up, as `coverNote` in `cover.asked` — and left it
            covering the whole submission. Owner, from a later round: *"if they
            have multiple subs its not individual for each class, should prob be
            by class."* Right, and it is D-354's own argument for a per-session
            *ask*, which the words travelling with it had not caught up to: one
            sentence went to the person taking ballet and the person taking tap.

            **What D-364 settled is unchanged** — who reads it, and that the
            cancellation reason is a different sentence for a different person
            with its own field below. Only where it lives has moved.

            The endpoint still accepts a submission-level note and falls back to
            it, so an older caller keeps working; nothing here sends one.
          */}

          {Boolean(date) && day.isPending && (
            <p className="py-4 text-center text-[13px] text-ink-3">Looking at that day...</p>
          )}

          {/*
            A failed lookup never renders as a statement about the day (D-209).
            "No classes" told to somebody with twelve is how she declares
            nothing and believes she has.
          */}
          {day.isError && (
            <div className="rounded-xl border border-line-2 bg-surface px-4 py-4 text-center">
              <p className="text-[13px] text-ink-2">We could not load that day just now.</p>
              <button
                type="button"
                onClick={() => void day.refetch()}
                className="mt-2 rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2"
              >
                Try again
              </button>
            </div>
          )}

          {day.isSuccess && sessions.length === 0 && (
            <p className="rounded-xl border border-line bg-surface-2 px-4 py-5 text-center text-[13px] text-ink-3">
              You are not teaching anything on {formatFullDate(date)}.
            </p>
          )}

          {day.isSuccess && sessions.length > 0 && (
            <div className="rounded-xl border border-line bg-surface-2 p-2">
              {/*
                Scrolls inside the dialog rather than growing it. Twelve rows on
                a 390px phone is taller than the screen, and a dialog whose
                submit button is below the fold is one she cannot finish.
              */}
              <ul className="flex max-h-[42vh] flex-col gap-1 overflow-y-auto">
                {sessions.map((s) => (
                  <SessionRow
                    key={s.occurrenceId}
                    session={s}
                    checked={!dropped.includes(s.occurrenceId)}
                    choice={kindOf(s.occurrenceId)}
                    onChoose={(kind) => setKind(s.occurrenceId, kind)}
                    staffId={choices[s.occurrenceId]?.staffId ?? ''}
                    onWho={(id) => setWho(s.occurrenceId, id)}
                    note={choices[s.occurrenceId]?.note ?? initialNote ?? ''}
                    onNote={(text) => setNoteFor(s.occurrenceId, text)}
                    /*
                      Only who is free for this session (D-597); the server
                      names the sessions each colleague is busy for.

                      **And nobody who has recorded time off that day** (D-681).
                      Away is the hard half of availability: a colleague outside
                      her published hours stays askable, because she may have
                      arranged something privately — but away is away, and
                      `CoverKind` refuses her. Offering her here would be a name
                      the save rejects, which is the D-424 failure this list has
                      already had once.
                    */
                    colleagues={(colleagues.data ?? []).filter(
                      (c) => !c.away && !c.busyFor?.includes(s.occurrenceId),
                    )}
                    colleaguesPending={asking && colleagues.isPending}
                    colleaguesFailed={colleagues.isError}
                    /*
                      **What she has already arranged, and what she can still
                      do about it** (D-365). This is the half that lets the
                      panel below her week go: a declared row used to be struck
                      through and inert, so *ask somebody else* after a decline
                      had to live somewhere else entirely.
                    */
                    onSorted={sorted}
                    onToggle={() =>
                      setDropped((prev) =>
                        prev.includes(s.occurrenceId)
                          ? prev.filter((id) => id !== s.occurrenceId)
                          : [...prev, s.occurrenceId],
                      )
                    }
                  />
                ))}
              </ul>
            </div>
          )}

          {run.isError && (
            <p role="alert" className="text-[12.5px] text-crit">
              {(run.error as Error).message}
            </p>
          )}

          {/*
            Said only when it differs from what she asked for. Reporting "12 of
            12 went through" on every submission is a dialog that congratulates
            itself, and it trains her to dismiss the one time it matters.
          */}
          {outcome && (
            <div role="status" className="rounded-xl border border-warn bg-warn-bg px-3 py-2.5">
              <p className="text-[12.5px] font-semibold">
                {/*
                  **Counted from the rows that were refused**, not from
                  `declared`, which counts declarations written and is a
                  different question once a row can cancel instead (D-361).
                */}
                {outcome.sessions.filter((s) => s.alreadyHandled).length === outcome.sessions.length
                  ? 'Nothing went through.'
                  : `${outcome.sessions.filter((s) => s.alreadyHandled).length} of ${outcome.sessions.length} did not go through.`}
              </p>

              <ul className="mt-1 flex flex-col gap-0.5">
                {outcome.sessions
                  .filter((s) => s.alreadyHandled)
                  .map((s) => (
                    <li key={s.occurrenceId} className="text-[11.5px] text-ink-2 wrap-anywhere">
                      {s.className} &mdash; {s.alreadyHandled}
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {/*
            **Whether whoever she asks is reminded** (D-554). On unless she
            says otherwise: a reminder two days on, and again two days before
            the class, is the timely poke the owner asked for - and a colleague
            she has already texted does not need one, which is what the box is
            for. Shown only when somebody is being asked; nothing else is
            reminded about.
          */}
          {rows.some((r) => r.askStaffId) && (
            <label className="flex items-center gap-2 text-[12.5px] text-ink-2">
              <input
                type="checkbox"
                checked={nudges}
                onChange={(e) => setNudges(e.target.checked)}
                className="h-4 w-4 accent-accent"
              />
              {text(content, 'asks.nudges.switch', 'Remind them if they have not answered')}
            </label>
          )}

          {/*
            **Only when something is being called off, and it is not the note**
            (D-364). The note goes to whoever picks the class up; this goes to
            Tonya and to nobody else (D-355). Merging them would email a
            cancellation justification to a colleague.
          */}

          {/*
            **The number before the send** (D-356, D-362).

            One figure across every row she is calling off, counted once per
            person — the server does it because the client cannot: a per-class
            preview returns a count and no ids, so summing them double-counts a
            parent with children in two of the cancelled classes.
          */}

          {/*
            **The thing she has to reach stays reachable, however much grows
            above it** (D-361).

            `observed` in Edge at 390&times;844, signed in as a real teacher on a day
            with twelve rows in the list: at rest the dialog is 760px inside a 758px box and fits.
            Choosing *Cancel it* adds the reason field, the content becomes
            870px, and the submit button lands at **y=853 with the dialog
            scrolled to the top** — below the fold, with nothing on screen
            saying to scroll.

            **That is D-354's own defect recurring in this same dialog**: a
            caveat present on a quiet day and invisible on the busy one. It was
            found by rendering it and by nothing else — the deep-audit walk
            reported `ok`, because sideways scroll and tap targets are not what
            went wrong.

            The confirmation is **inside** the sticky block rather than above
            it, deliberately: it is the number she is consenting to, and a
            button that sticks while the figure it belongs to scrolls away is
            worse than either alone.
          */}
          <div className="sticky bottom-0 -mx-5 -mb-5 flex flex-col gap-2 bg-surface px-5 pt-2 pb-5">
          {/*
            Inside the sticky block with the figure and the button (D-361):
            above it, the footer painted over the box's lower half whenever
            the dialog was not scrolled to the very bottom - "the box at the
            bottom seems to be cut off", asked from QA. It is part of what she
            is consenting to, so it belongs with the consent anyway.
          */}
          {cancelling.length > 0 && (
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-semibold text-ink-3">
                Why are you calling {cancelling.length === 1 ? 'it' : 'them'} off? Tonya sees this
              </span>
              <textarea
                value={reason}
                onChange={(e) => {
                  // No need to clear the held preview here: it is keyed on the
                  // submission, and the reason is part of that signature.
                  setReason(e.target.value)
                }}
                rows={2}
                placeholder="Nobody could cover it"
                className={field}
              />
            </label>
          )}
          {cancelling.length > 0 && (
            /*
              The families' half of the same question (D-659), beside Tonya's
              rather than instead of it. Optional: left empty, the email is the
              one everybody is used to. One note covers the whole submission,
              because a family hears about one night rather than about a row.
            */
            <label className="flex flex-col gap-1">
              <span className="text-[12px] font-semibold text-ink-3">
                Anything to tell the families? Goes in their email
              </span>
              <textarea
                value={familyNote}
                onChange={(e) => setFamilyNote(e.target.value)}
                rows={2}
                placeholder="Optional"
                className={field}
              />
            </label>
          )}
          {shown && (
            <div role="status" className="rounded-xl border border-crit bg-crit-bg px-3 py-2.5">
              <p className="text-[12.5px] font-semibold">
                {shown.cancelling === 1
                  ? '1 class will be called off.'
                  : `${shown.cancelling} classes will be called off.`}{' '}
                {/* Adults with an address, not dancers: "17 people" for a class
                    of eight read as a bug, `asked`. The server counts guardians. */}
                {shown.families === 1
                  ? '1 family member will be told.'
                  : `${shown.families} family members will be told.`}
              </p>
              <p className="mt-0.5 text-[11.5px] text-ink-2">This cannot be undone from here.</p>
            </div>
          )}
          <div className="mt-1 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-3 py-2 text-[13px] font-semibold text-ink-3 hover:text-ink"
            >
              Cancel
            </button>

            <button
              type="button"
              data-testid="declare-submit"
              disabled={chosen.length === 0 || run.isPending}
              onClick={submitting}
              className="rounded-lg bg-accent px-4 py-2 text-[13px] font-semibold text-on-accent disabled:opacity-45"
            >
              {/*
                **The label describes the whole submission** (D-361).

                It always read "I am out for N classes", which stopped being
                true the moment one of the N was a cancellation — a button that
                names half of what it is about to do, on the half that reaches
                sixty households.
              */}
              {run.isPending
                ? 'Saying so...'
                : shown
                  ? 'Yes, call them off'
                  : cancelling.length === 0
                    ? chosen.length === 1
                      ? 'I am out for 1 class'
                      : `I am out for ${chosen.length} classes`
                    : cancelling.length === chosen.length
                      ? `Call off ${chosen.length === 1 ? 'this class' : `all ${chosen.length}`}`
                      : `Out for ${chosen.length - cancelling.length}, calling off ${cancelling.length}`}
            </button>
          </div>

          {/*
            Said once, here, because it is the thing she is most likely to
            assume wrongly — and assuming it wrongly means a room of dancers
            turning up to nobody.

            **It speaks only over the rows it is true of** (D-361). Once every
            ticked row says *Cancel it*, "this does not cancel the class" is
            simply false, and a caveat that is false on the busiest submission
            is worse than none.
          */}
          {chosen.length > cancelling.length && (
            <p className="text-[11.5px] text-ink-3">
              This tells the studio you are out.{' '}
              {cancelling.length === 0
                ? 'It does not cancel the class.'
                : 'It does not cancel the classes you have not called off.'}
            </p>
          )}
          </div>

        </div>
      </div>
    </div>
  )
}

function SessionRow({
  session,
  checked,
  choice,
  onChoose,
  staffId,
  onWho,
  colleagues,
  colleaguesPending,
  colleaguesFailed,
  onSorted,
  onToggle,
  note,
  onNote,
}: {
  session: DaySession
  checked: boolean
  /** What whoever takes *this* class should know (D-375). */
  note: string
  onNote: (note: string) => void
  choice: Choice
  onChoose: (kind: Choice) => void
  staffId: string
  onWho: (staffId: string) => void
  colleagues: Colleague[]
  /** Still loading. The list is fetched lazily, so this is the first render. */
  colleaguesPending: boolean
  /** The lookup failed, which must never render as "there is nobody" (D-209). */
  colleaguesFailed: boolean
  /** Something about an already-declared row changed; refetch the day. */
  onSorted: () => void
  onToggle: () => void
}) {
  const handled = session.alreadyHandled
  const live = !handled && checked

  /*
   * **She has already said she is out for this one, and it is still hers to
   * sort out** (D-365).
   *
   * Not the same as `handled`: a row can be untickable because it is somebody
   * else's class or because the day has passed, and neither gives her anything
   * to do. This is the one case that does — and it is the bucket that kept
   * `CoverPanel` alive, because a struck-through row is a dead end.
   *
   * **`!= null`, not `!== null`**, and the difference is not style. The server
   * sends `null`; anything that has not been through it — an older payload, a
   * fixture, a cached row from before this shipped — has no key at all, and
   * `undefined !== null` is **true**. Strict equality made every such row read
   * as declared and then handed `coverSummary` an undefined state to turn into
   * a sentence. Caught by an existing test, not by reading it.
   */
  const declared = session.coverRequestId != null && session.coverState != null

  /*
   * **Declared, still happening, and not already in the past.**
   *
   * `CannotDeclare` puts *the day has passed* above *already declared*, so a
   * past session she declared carries both a live request and that sentence —
   * and every cover control on it hits `HasStartedAsync` and is refused. The
   * date field has no floor, so a past day is one tap away.
   *
   * Compared against the row's own date rather than parsed out of
   * `alreadyHandled`: matching on a sentence is matching on wording that
   * somebody will reasonably reword.
   */
  const actionable = declared && !session.isCancelled && session.date >= today()

  return (
    <li className="rounded-lg py-0.5">
      <label
        className={`flex min-h-[44px] items-center gap-2.5 rounded-lg px-2 py-1.5 ${
          handled ? 'opacity-55' : 'hover:bg-surface'
        }`}
      >
        <input
          type="checkbox"
          checked={handled ? false : checked}
          disabled={Boolean(handled)}
          onChange={onToggle}
          className="size-4 shrink-0 accent-[var(--accent)]"
        />

        <span className="min-w-0 flex-1">
          {/*
            `wrap-anywhere`: a Jackrabbit class name is one unbreakable word to
            CSS, and the part that distinguishes it is at the end (D-115).
          */}
          <span
            className={`block text-[13px] font-semibold wrap-anywhere ${
              handled ? 'text-ink-3 line-through' : 'text-ink'
            }`}
          >
            {session.className}
          </span>

          <span className="block text-[11.5px] text-ink-3">
            {session.startTime
              ? `${clockTime(session.startTime)}${meridiem(session.startTime)}`
              : 'No time set'}
            {session.roomName && ` · ${session.roomName}`}
            {` · ${session.enrolled} ${session.enrolled === 1 ? 'dancer' : 'dancers'}`}
          </span>

          {/* Shown, never silently dropped. */}
          {/*
            **What it is waiting for, not merely that it cannot be ticked**
            (D-365).

            `alreadyHandled` says *you have already said you are out for this
            one*, which is true and useless: it describes the control she is
            looking at, not whether anybody is taking the class. The state is
            the answer to the question she actually has — and `coverSummary` is
            the one function that turns a state into a sentence, because three
            screens ask it and two implementations of "what does this mean" is
            how a screen comes to name the wrong thing confidently (D-261).
          */}
          {/*
            **Both, when both are true.** The first cut suppressed this
            whenever the row was declared — which is right for *"you have
            already said you are out for this one"*, since the state below says
            it better, and wrong for every other reason. A past session she
            declared carries *"that session has already happened"* **and** a
            live request, and hiding the first left *"Nobody covering yet"* over
            a class that already ran (D-209).

            **Derived, not matched on the wording.** `CannotDeclare`'s ladder
            puts the date above the declaration, so on a future row the only
            reason that can be present is *already declared* — which the state
            says better — and on a past row it is the date, which the state does
            not say at all. Matching the sentence would be matching a string
            somebody will reasonably reword.
          */}
          {handled && !(declared && session.date >= today()) && (
            <span className="block text-[11.5px] text-ink-3">{handled}</span>
          )}

          {declared && (
            <span className="block text-[11.5px] text-ink-2">
              {session.isCancelled
                ? 'Called off.'
                : coverSummary(
                    {
                      state: session.coverState!,
                      askedName: session.askedName,
                      declineReason: session.declineReason,
                    } as CoverRow,
                    true,
                  )}
            </span>
          )}
        </span>
      </label>

      {/*
        **What happens to this one** (D-361).

        Radios rather than two tick-boxes, because the choices are exclusive in
        the domain and an input shape that can express a state the server cannot
        hold is an input shape that lies. Grouped by `name` on the occurrence id
        so the exclusivity is per row: twelve rows are twelve independent
        three-way choices, which is the whole reason the owner's "one name for
        the day" objection (D-354) does not apply here.

        **Disabled when the row is not ticked**, not hidden — a control that
        vanishes as you untick makes the list jump under the thumb on a
        twelve-row Monday, and the row still needs to say what it *would* do.
      */}
      {!handled && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 pb-1.5 pl-9">
          {CHOICES.map(({ value, label }) => (
            <label
              key={value}
              className={`flex min-h-[24px] items-center gap-1.5 text-[12px] ${
                live ? 'text-ink-2' : 'text-ink-3 opacity-55'
              }`}
            >
              <input
                type="radio"
                name={`choice-${session.occurrenceId}`}
                value={value}
                checked={choice === value}
                disabled={!live}
                onChange={() => onChoose(value)}
                className="size-3.5 shrink-0 accent-[var(--accent)]"
              />
              {label}
            </label>
          ))}
        </div>
      )}

      {/*
        **Who, on the row that names her** (D-361).

        D-354 kept this off the day grid because "one name applied to twelve
        rows would be a lie that is quicker to type than the truth" - a name
        **per row** is exactly what that objection asks for, so this answers it
        rather than reversing it.

        **Only once she has chosen to ask.** Twelve dropdowns on a twelve-row
        Monday, for a teacher who named nobody, is the clutter the dialog exists
        to remove.
      */}
      {!handled && live && choice === 'ask' && (
        <div className="pb-2 pl-9">
          <select
            aria-label={`Who should take ${session.className}?`}
            value={staffId}
            onChange={(e) => onWho(e.target.value)}
            className="w-full max-w-[16rem] rounded-lg border border-line-2 bg-surface px-2 py-1.5 text-[12.5px] text-ink"
          >
            {/*
              **Three states, because "still loading" and "could not ask" are
              different facts and only one of them can be described** (D-209).

              The list is fetched lazily, so an empty picker is the *normal*
              first render rather than a rare one — and an empty picker with no
              explanation reads as a statement about the studio's staff: *there
              is nobody to ask*. The row then lands as an ordinary uncovered
              declaration and she never learns why.

              The same two branches `CoverPanel` already renders off this same
              query key, lifted rather than answered a third time.
            */}
            <option value="">
              {colleaguesPending
                ? 'Loading...'
                : colleaguesFailed
                  ? 'Could not load your colleagues'
                  : 'Nobody yet'}
            </option>
            {colleagues.map((c) => (
              <option key={c.id} value={c.id}>
                {/*
                  **Words in the option, not a colour or a title** (D-708): an
                  option renders text and nothing else, and the mark has to be
                  readable in the closed select once she is picked. A mark, not a
                  filter - she stays selectable, because she may well say yes.
                */}
                {c.name}
                {c.outsideHoursFor?.includes(session.occurrenceId) ? ' (outside her usual hours)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {/*
        **What whoever takes *this* class should know** (D-375).

        Owner: *"if they have multiple subs its not individual for each class,
        should prob be by class."* It was one field on the whole submission, so
        a Tuesday with five classes sent the same sentence to the person taking
        ballet and the person taking tap — which is D-354's own argument for a
        per-session *ask*, and the words travelling with it had not caught up.

        **On rows that will actually reach somebody**, which is every ticked row
        except a cancellation: a class being called off has no substitute to
        read this, and its reason goes to Tonya through the separate field
        below (D-364).

        The list scrolls at `max-h-[42vh]` (D-354), so a field per row cannot
        push the submit button off a twelve-row Monday.
      */}
      {!handled && live && choice !== 'cancel' && (
        <div className="pb-2 pl-9">
          <input
            type="text"
            value={note}
            onChange={(e) => onNote(e.target.value)}
            aria-label={`Anything the person taking ${session.className} should know?`}
            placeholder="Anything they should know? (optional)"
            className="w-full rounded-lg border border-line-2 bg-surface px-2 py-1.5 text-[12.5px] text-ink placeholder:text-ink-3"
          />
        </div>
      )}

      {/*
        **The controls that used to live two screens down** (D-365).

        `CoverPanel` carried these on a card below her teaching week, which is
        the owner's *"clunky and would be missed"*. They belong on the row they
        are about, in the dialog she opened to sort this day out — and they are
        the *same components*, imported rather than rebuilt, because a panel and
        a dialog holding two copies of this idea would disagree first about the
        cancelled case (D-199).

        **Ask and call-it-off are one decision tried in order**, which is why
        the order on the row is the order to try them in: cancelling is the
        fallback after the sub search fails (D-355), and the label says which is
        the last resort.
      */}
      {/*
        **Gated on the row being actionable, not merely on it being declared**
        (D-365).

        `CannotDeclare` orders *the day has passed* above *already declared*, so
        a past session she declared arrives with both a live request and
        "that session has already happened" — and every control here then hits
        `HasStartedAsync` and comes back refused. A row whose only outcome is a
        refusal should not offer the control (D-334).
      */}
      {actionable && (
        <div className="pb-2 pl-9">
          <AskSomebody
            requestId={session.coverRequestId!}
            state={session.coverState!}
            onAsked={onSorted}
          />
          <CallItOff
            occurrenceId={session.occurrenceId}
            state={session.coverState!}
            onDone={onSorted}
          />
          <ComingAfterAll
            requestId={session.coverRequestId!}
            state={session.coverState!}
            onDone={onSorted}
          />
        </div>
      )}

      {/*
        **Keyed on the cancellation, never on the declaration** (D-365).

        The first cut required `declared`, which cannot be true here: cancelling
        withdraws every live request for the session (D-355), and `declared`
        needs one. So *put it back on* rendered for nobody, and the two tests
        covering it used a payload the server cannot produce — the fixture that
        makes the test pass being the fixture that hides the bug, exactly.
        `reinstateSession` would then have lost its only caller when the panel
        goes (D-332).
      */}
      {session.isCancelled && (
        <div className="pb-2 pl-9">
          <CalledOff
            occurrenceId={session.occurrenceId}
            cancelledByMe={session.cancelledByMe}
            onDone={onSorted}
          />
        </div>
      )}
    </li>
  )
}
